import { jwtDecode } from 'jwt-decode';
import { AuthError } from '../utils/auth-error';

const ACCESS_TOKEN = 'rh_access_token';
const ID_TOKEN = 'rh_id_token';
const REFRESH_TOKEN = 'rh_refresh_token';

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN;
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID;
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE;
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const REALM = 'Username-Password-Authentication';
const DEFAULT_SCOPE = 'openid profile email offline_access';

export interface NormalizedTokens {
  accessToken: string;
  idToken: string;
  refreshToken?: string;
}

export type SocialConnection = 'google-oauth2' | 'apple';

class AuthenticationService {
  private static instance: AuthenticationService;

  private constructor() {}

  public static getInstance(): AuthenticationService {
    if (!AuthenticationService.instance) {
      AuthenticationService.instance = new AuthenticationService();
    }
    return AuthenticationService.instance;
  }

  private persistTokens(tokens: NormalizedTokens): void {
    if (!tokens?.accessToken || !tokens?.idToken) {
      throw new Error('Missing accessToken or idToken');
    }
    localStorage.setItem(ACCESS_TOKEN, tokens.accessToken);
    localStorage.setItem(ID_TOKEN, tokens.idToken);
    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN, tokens.refreshToken);
    }
    window.dispatchEvent(new Event('rh-auth-change'));
  }

  public async loginWithPassword(email: string, password: string): Promise<void> {
    const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        grant_type: 'http://auth0.com/oauth/grant-type/password-realm',
        username: email,
        password,
        realm: REALM,
        client_id: AUTH0_CLIENT_ID,
        audience: AUTH0_AUDIENCE,
        scope: DEFAULT_SCOPE,
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new AuthError(data?.error, data?.error_description);
    }

    try {
      const decoded: any = jwtDecode(data.id_token);
      if (decoded?.email_verified === false) {
        throw new AuthError('email_not_verified', 'Please verify your email before logging in.');
      }
    } catch (e) {
      if (e instanceof AuthError) throw e;
    }

    this.persistTokens({
      accessToken: data.access_token,
      idToken: data.id_token,
      refreshToken: data.refresh_token,
    });
  }

  public async signup(email: string, password: string, name?: string): Promise<void> {
    const response = await fetch(`https://${AUTH0_DOMAIN}/dbconnections/signup`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: AUTH0_CLIENT_ID,
        email,
        password,
        connection: REALM,
        ...(name ? { name } : {}),
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      const description =
        typeof data?.description === 'string'
          ? data.description
          : data?.message || data?.error_description || data?.policy || 'Sign up failed. Please try again.';
      throw new AuthError(data?.code || data?.error, description);
    }
  }

  public async resetPassword(email: string): Promise<void> {
    const response = await fetch(`https://${AUTH0_DOMAIN}/dbconnections/change_password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id: AUTH0_CLIENT_ID,
        email,
        connection: REALM,
      }),
    });

    if (!response.ok) {
      let description = 'Failed to send reset email. Please try again.';
      try {
        const data = await response.json();
        description = data?.description || data?.error_description || description;
      } catch {}
      throw new AuthError('reset_failed', description);
    }
  }

  public async getLoginMethod(email: string): Promise<'google' | 'apple' | 'email'> {
    if (!API_BASE_URL || !email) return 'email';
    try {
      const url = `${API_BASE_URL}/webhook/login-method?email=${encodeURIComponent(email.trim())}`;
      const response = await fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } });
      if (!response.ok) return 'email';
      const data = await response.json().catch(() => ({}));
      if (data?.method === 'google') return 'google';
      if (data?.method === 'apple') return 'apple';
      return 'email';
    } catch {
      return 'email';
    }
  }

  public async completeSocialLogin(tokens: NormalizedTokens): Promise<void> {
    this.persistTokens(tokens);
  }

  public getAccessToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN);
  }

  public getUserInfo(): any | null {
    const idToken = localStorage.getItem(ID_TOKEN);
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    if (!idToken) return null;

    try {
      const decoded: any = jwtDecode(idToken);
      let roles: string[] = [];

      const audience = AUTH0_AUDIENCE || '';
      let roleBase = audience.endsWith('/') ? audience.slice(0, -1) : audience;
      if (!roleBase.endsWith('/api')) roleBase = roleBase + '/api';
      const claimKey = `${roleBase}/roles`;

      roles = decoded[claimKey] || [];

      if ((!roles || roles.length === 0) && accessToken) {
        try {
          const decodedAccess: any = jwtDecode(accessToken);
          roles = decodedAccess[claimKey] || [];
        } catch {}
      }

      return { ...decoded, roles };
    } catch {
      return null;
    }
  }

  public isAuthenticated(): boolean {
    const accessToken = localStorage.getItem(ACCESS_TOKEN);
    const idToken = localStorage.getItem(ID_TOKEN);
    if (!accessToken || !idToken) return false;

    try {
      const decoded: any = jwtDecode(idToken);
      if (decoded.exp) {
        return decoded.exp * 1000 > Date.now();
      }
      return true;
    } catch {
      return false;
    }
  }

  public async silentRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN);
    if (!refreshToken) return false;

    try {
      const response = await fetch(`https://${AUTH0_DOMAIN}/oauth/token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          grant_type: 'refresh_token',
          client_id: AUTH0_CLIENT_ID,
          refresh_token: refreshToken,
          scope: DEFAULT_SCOPE,
        }),
      });

      if (!response.ok) return false;
      const data = await response.json();
      if (!data?.access_token) return false;

      localStorage.setItem(ACCESS_TOKEN, data.access_token);
      if (data.id_token) localStorage.setItem(ID_TOKEN, data.id_token);
      if (data.refresh_token) localStorage.setItem(REFRESH_TOKEN, data.refresh_token);
      return true;
    } catch {
      return false;
    }
  }

  public logout(): void {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(ID_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    window.dispatchEvent(new Event('rh-auth-change'));
  }
}

export const authService = AuthenticationService.getInstance();
